import { useMemo } from 'react';
import { formatDurationMts, resolveOrderServiceDuration } from '@/api/modules/order';
import {
  computeEqualDurationAllocations,
  listOrderAssignedStaff,
} from '@/lib/settleAllocation';
import type { Order } from '@/types/order';

function formatSplitHours(hours: number): string {
  const rounded = Math.round(hours * 100) / 100;
  return `${rounded.toFixed(2)} h`;
}

interface StaffHourAllocationProps {
  order: Pick<Order, 'servicers' | 'assignees' | 'serviceDuration' | 'cleanItems'>;
  /** 预览用总时长；不传则用订单当前总时长 */
  totalHours?: number;
  /** 个人工时标 MTS。总时长不要使用这个标记。 */
  markPersonalMts?: boolean;
}

export function StaffHourAllocation({ order, totalHours, markPersonalMts = false }: StaffHourAllocationProps) {
  const staff = useMemo(() => listOrderAssignedStaff(order), [order]);
  const hours = totalHours ?? resolveOrderServiceDuration(order.serviceDuration, order.cleanItems) ?? 0;
  const split = useMemo(
    () => computeEqualDurationAllocations(hours, staff),
    [hours, staff],
  );

  if (staff.length === 0 || hours <= 0) return null;

  return (
    <div>
      <div className="text-[10px] text-gray-400 font-medium uppercase tracking-wider mb-1.5 flex items-center gap-1.5">
        <i className="ri-group-line text-green-500 text-xs" />
        Staff Hours
      </div>
      <div className="space-y-1">
        {staff.map((person) => (
          <div key={person.operatorId} className="flex items-center justify-between gap-3 text-[11px] leading-relaxed">
            <span className="font-medium text-gray-700 min-w-0 flex-1 truncate">{person.name}</span>
            <span className="text-gray-800 font-semibold flex-shrink-0 tabular-nums">
              {markPersonalMts
                ? formatDurationMts(split[person.operatorId] ?? 0)
                : formatSplitHours(split[person.operatorId] ?? 0)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
